import { createRef, CSSProperties, PureComponent, ReactNode, RefObject } from "react";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import { AMAP_DEFAULT_CENTER, IAmapCenter, warnIfOutsideAmapCoverage } from "../../../shared/utilty/amap-coverage";
import { loadAMap } from "../../../shared/utilty/amap-loader";
import { gcj02ToWgs84, IGeoPoint, wgs84ToGcj02 } from "../../../shared/utilty/coordinate-transform";
import { CommonProps } from "../common-props";
import { LocationPickerProps } from "./location-picker";

/** Zoom the picker's map opens at, and the zoom the Amap coverage check is made against. */
const INITIAL_ZOOM: number = 11;

interface KuikaDesignerGlobals {
  isDesignTime?: boolean;
}

interface WindowWithKuikaGlobals {
  kuika?: KuikaDesignerGlobals;
}

const isDesignTime = (): boolean => (window as unknown as WindowWithKuikaGlobals).kuika?.isDesignTime === true;

interface IMapCoords {
  lat: number;
  lng: number;
}

// AMap.Map's "click" event callback receives the clicked point as `lnglat` (GCJ-02, the coordinate
// system Amap's JSAPI operates in). This is not declared by @amap/amap-jsapi-types, mirroring the
// same gap documented in amap-body-wrapper.tsx for this exact event.
interface IAMapMapMouseEvent {
  lnglat: AMap.LngLat;
}

interface AmapLocationPickerState {
  mapReady: boolean;
}

/**
 * Amap (AutoNavi JSAPI 2.0) equivalent of LocationPicker (location-picker.tsx). Unlike
 * google-map-react (a declarative React wrapper), Amap's JSAPI is imperative: the map instance is
 * created once in componentDidMount (after loadAMap() resolves) against a plain container div, and
 * the picker marker is created/moved imperatively as the `value` prop changes instead of being
 * expressed as a JSX child - the same shape handlePlaceMarker() uses for Google, just driven by
 * AMap.Marker instead of google.maps.Marker.
 *
 * Coordinate rule (K2): every value this component owns/receives from Kuika (props.value,
 * props.latitude/longitude, geolocation results) is WGS-84. Amap's map and markers operate in
 * GCJ-02, so wgs84ToGcj02 is applied to every coordinate handed to Amap (initial center, marker
 * position, setMapCenterToUserLocation), and gcj02ToWgs84 is applied to every coordinate read back
 * from Amap before it is reported to Kuika (map click, marker position read back via getLatitude/
 * getLongitude).
 */
class AmapLocationPicker extends PureComponent<LocationPickerProps & CommonProps, AmapLocationPickerState> {
  private containerRef: RefObject<HTMLDivElement | null> = createRef();

  private amapNs: typeof AMap | undefined;

  private map: AMap.Map | undefined;

  private markerRef: AMap.Marker | undefined;

  private latPropBeforeMapLoaded: number | null | undefined;

  private lngPropBeforeMapLoaded: number | null | undefined;

  constructor(props: LocationPickerProps & CommonProps) {
    super(props);
    this.state = { mapReady: false };
    this.latPropBeforeMapLoaded = props.value?.latitude ?? props.latitude;
    this.lngPropBeforeMapLoaded = props.value?.longitude ?? props.longitude;
  }

  componentDidMount(): void {
    this.initializeMap();
  }

  componentWillUnmount(): void {
    this.map?.off("click", this.handleMapClick);
    this.markerRef?.setMap(null);
    this.map?.destroy();
    this.map = undefined;
    this.markerRef = undefined;
    this.amapNs = undefined;
  }

  componentDidUpdate(): void {
    this.handlePlaceMarker();
  }

  private async initializeMap(): Promise<void> {
    let amapNs: typeof AMap;
    try {
      amapNs = await loadAMap();
    } catch (error) {
      console.error("Error loading Amap JSAPI:", error);
      return;
    }

    if (!this.containerRef.current) return;
    this.amapNs = amapNs;

    // An unbound picker used to center on 0,0 — open ocean outside Amap's mainland-China
    // coverage, so at INITIAL_ZOOM the tile service serves nothing and the map renders as an
    // empty grid. Fall back to Amap's own default center instead (shared/utilty/amap-coverage.ts).
    const boundLatitude: number | null | undefined = this.getLatitude();
    const boundLongitude: number | null | undefined = this.getLongitude();
    const boundCenter: IAmapCenter | null =
      typeof boundLatitude === "number" && typeof boundLongitude === "number"
        ? { latitude: boundLatitude, longitude: boundLongitude }
        : null;
    const center: IAmapCenter = boundCenter ?? AMAP_DEFAULT_CENTER;
    const gcjCenter: IGeoPoint = wgs84ToGcj02(center.latitude, center.longitude);

    warnIfOutsideAmapCoverage(center.latitude, center.longitude, INITIAL_ZOOM);

    this.map = new amapNs.Map(this.containerRef.current, {
      center: [gcjCenter.longitude, gcjCenter.latitude],
      zoom: INITIAL_ZOOM,
      doubleClickZoom: false
    });

    this.map.on("click", this.handleMapClick);

    this.handlePlaceMarker();
    this.handleAmapApiLoaded();
  }

  private handleAmapApiLoaded = (): void => {
    const latitude: number | null | undefined = this.getLatitude();
    const longitude: number | null | undefined = this.getLongitude();

    this.latPropBeforeMapLoaded = null;
    this.lngPropBeforeMapLoaded = null;

    this.setState({ mapReady: true }, () => {
      if (typeof latitude === "number" || typeof longitude === "number") {
        this.props.onChange?.({ latitude: latitude || 0, longitude: longitude || 0 });
        return;
      }

      this.setMapCenterToUserLocation();
    });
  };

  private handleMapClick = (e: IAMapMapMouseEvent): void => {
    const wgs84: IGeoPoint = gcj02ToWgs84(e.lnglat.lat, e.lnglat.lng);
    this.handleMapOnClick({ lat: wgs84.latitude, lng: wgs84.longitude });
  };

  handleMapOnClick = ({ lat, lng }: IMapCoords): void => {
    if (this.map) {
      this.props.onClick?.({ latitude: lat, longitude: lng });
      this.props.onChange?.({ latitude: lat, longitude: lng });
    } else {
      this.latPropBeforeMapLoaded = lat;
      this.lngPropBeforeMapLoaded = lng;
    }
  };

  handlePlaceMarker = (): void => {
    const { value } = this.props;
    if (this.map && this.amapNs) {
      if (this.markerRef) {
        if (typeof value?.latitude !== "number" && typeof value?.longitude !== "number") {
          this.markerRef.setMap(null);
          this.markerRef = undefined;
          return;
        }

        const gcj: IGeoPoint = wgs84ToGcj02(value?.latitude || 0, value?.longitude || 0);
        this.markerRef.setPosition([gcj.longitude, gcj.latitude]);
        return;
      }

      if (typeof value?.latitude !== "number" && typeof value?.longitude !== "number") {
        return;
      }

      const gcj: IGeoPoint = wgs84ToGcj02(value?.latitude || 0, value?.longitude || 0);
      const marker: AMap.Marker = new this.amapNs.Marker({
        position: [gcj.longitude, gcj.latitude],
        map: this.map
      });
      this.markerRef = marker;
      return;
    }

    this.latPropBeforeMapLoaded = value?.latitude ?? this.latPropBeforeMapLoaded;
    this.lngPropBeforeMapLoaded = value?.longitude ?? this.lngPropBeforeMapLoaded;
  };

  setMapCenterToUserLocation = (): void => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(({ coords: { latitude, longitude } }: GeolocationPosition) => {
        if (!this.map) return;
        // Only reached right after init (no bound value), so the map is still at INITIAL_ZOOM.
        // A user outside mainland China lands on refused tiles here even though the default
        // center rendered fine — warn so that transition is not a silent blank map.
        warnIfOutsideAmapCoverage(latitude, longitude, INITIAL_ZOOM);
        const gcj: IGeoPoint = wgs84ToGcj02(latitude, longitude);
        this.map.setCenter([gcj.longitude, gcj.latitude]);
      });
    }
  };

  getStyleProp = (): CSSProperties => {
    let style: CSSProperties = {};
    if (this.props.style) {
      style = { ...this.props.style };
    }
    if (!style.height) {
      style.height = "400px";
    }
    if (!style.width) {
      style.width = "100%";
    }
    style.display = "inline-flex";

    // Must be relative
    style.position = "relative";

    return style;
  };

  renderDesignTime(): ReactNode {
    return <div className="googleMapsMock"></div>;
  }

  getLatitude = (): number | null | undefined => {
    if (!this.markerRef) {
      return this.latPropBeforeMapLoaded;
    }
    const position: AMap.LngLat | null = this.markerRef.getPosition();
    if (!position) {
      return this.latPropBeforeMapLoaded;
    }
    return gcj02ToWgs84(position.lat, position.lng).latitude;
  };

  getLongitude = (): number | null | undefined => {
    if (!this.markerRef) {
      return this.lngPropBeforeMapLoaded;
    }
    const position: AMap.LngLat | null = this.markerRef.getPosition();
    if (!position) {
      return this.lngPropBeforeMapLoaded;
    }
    return gcj02ToWgs84(position.lat, position.lng).longitude;
  };

  renderRunTime(): ReactNode {
    return <div data-testid="amap-location-picker" ref={this.containerRef} style={{ width: "100%", height: "100%" }} />;
  }

  render(): ReactNode {
    const designTime: boolean = isDesignTime();

    return (
      <div style={this.getStyleProp()} className={this.props.className}>
        {designTime ? this.renderDesignTime() : this.renderRunTime()}
      </div>
    );
  }
}

const amapLocationPicker = withCommonEvents(AmapLocationPicker);

// `AmapLocationPickerRaw` (the bare class, no CommonProps handling) is exported so
// location-picker.tsx can compose it directly into its own withCommonEvents-wrapped provider
// selector without wrapping it a second time - double-wrapping would apply disabled-style,
// onClick remapping and kuikaRef attachment twice. Everyone else should use the default,
// fully self-contained `AmapLocationPicker` export below.
export { AmapLocationPicker as AmapLocationPickerRaw, amapLocationPicker as AmapLocationPicker };
